import { LucideIcon, Settings } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Fragment } from "react";
import CollapsedNavItem from "@/components/navigation/CollapsedNavItem";
import NavItem from "@/components/navigation/NavItem";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FuerDichBell } from "@/components/notifications/FuerDichBell";

export interface LinkItem {
  title: string;
  label?: string;
  icon: LucideIcon;
  variant: "default" | "ghost";
  target: string;
  /** Run this action on click instead of navigating to `target`. */
  action?: "archiveSearch";
  visible?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  staffOnly?: boolean;
  statusDot?: boolean;
  online?: boolean;
  children?: LinkItem[];
}

interface NavProps {
  className?: string;
  topLinks: LinkItem[];
  bottomLinks: LinkItem[];
  isCollapsed: boolean;
  /** Neptune live-connection state, applied to whichever link has `statusDot: true`. */
  agentOnline?: boolean;
  /** The settings page holds admin content only; a non-admin reaches the account page from the user menu. */
  showSettings: boolean;
}

export function Nav({
  className,
  topLinks,
  bottomLinks,
  isCollapsed,
  agentOnline = false,
  showSettings,
}: NavProps) {
  const location = useLocation();
  const { pathname } = location;
  const { t } = useTranslation();

  // In the grouped staff section we drop the per-item orange (the section label
  // + accent border carries the "staff only" signal instead). On the collapsed
  // rail there is no label, so staff icons keep their orange tint.
  const renderLinks = (links: LinkItem[], suppressStaffColor = false) => (
    <>
      {links
        .filter((link) => link.visible !== false)
        .map((rawLink) => {
          const link = rawLink.statusDot
            ? { ...rawLink, online: agentOnline }
            : rawLink;
          const children = (link.children ?? []).filter(
            (child) => child.visible !== false,
          );
          if (isCollapsed) {
            // Collapsed rail has no room for nesting — show parent + children flat.
            return (
              <Fragment key={link.target}>
                <CollapsedNavItem pathname={pathname} link={link} />
                {children.map((child) => (
                  <CollapsedNavItem
                    key={child.target}
                    pathname={pathname}
                    link={child}
                  />
                ))}
              </Fragment>
            );
          }
          return (
            <Fragment key={link.target}>
              <NavItem
                pathname={pathname}
                link={link}
                suppressStaffColor={suppressStaffColor}
              />
              {children.length > 0 && (
                <div className="ml-4 grid gap-1 border-l pl-2">
                  {children.map((child) => (
                    <NavItem
                      key={child.target}
                      pathname={pathname}
                      link={child}
                      suppressStaffColor={suppressStaffColor}
                    />
                  ))}
                </div>
              )}
            </Fragment>
          );
        })}
    </>
  );

  const isStaff = (link: LinkItem) => link.staffOnly === true;
  const topRegular = topLinks.filter((l) => !isStaff(l));
  const bottomRegular = bottomLinks.filter((l) => !isStaff(l));
  // Collect every staff-only entry (from both lists) into one labeled group.
  const staffLinks = [...topLinks, ...bottomLinks].filter(isStaff);
  const hasStaffSection =
    staffLinks.some((l) => l.visible !== false);

  return (
    <ScrollArea
      className={cn("[&>div>div]:!h-full [&>div>div]:!block", className)}
    >
      <div
        data-collapsed={isCollapsed}
        className="h-full group flex flex-col gap-4 py-2"
      >
        <nav className="grid gap-1 px-2 group-[[data-collapsed=true]]:justify-center">
          <FuerDichBell isCollapsed={isCollapsed} />
          {renderLinks(topRegular)}
        </nav>
        {hasStaffSection &&
          (isCollapsed ? (
            <nav className="grid gap-1 px-2 justify-center border-t border-[rgb(249_115_22)]/30 pt-3">
              {renderLinks(staffLinks)}
            </nav>
          ) : (
            <nav className="grid gap-1 px-2">
              <div className="flex items-center gap-2 px-3 pb-1 pt-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-[rgb(249_115_22)]">
                  {t("nav.staffSection")}
                </span>
                <div className="h-px flex-1 bg-[rgb(249_115_22)]/30" />
              </div>
              <div className="border-l-2 border-[rgb(249_115_22)]/40 pl-2">
                <div className="grid gap-1">{renderLinks(staffLinks, true)}</div>
              </div>
            </nav>
          ))}
        <nav className="mt-auto grid gap-1 px-2 group-[[data-collapsed=true]]:justify-center">
          {renderLinks(bottomRegular)}
          {!showSettings ? null : isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Link
                  to="/settings"
                  className={cn(
                    buttonVariants({
                      variant: pathname == "/settings" ? "secondary" : "ghost",
                      size: "icon",
                    }),
                    "h-9 w-9",
                  )}
                  style={
                    pathname == "/settings"
                      ? {
                          backgroundColor: "var(--color-secondary)",
                          color: "var(--color-secondary-foreground)",
                        }
                      : undefined
                  }
                >
                  <Settings className="h-4 w-4" />
                  <span className="sr-only">{t("nav.settings")}</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex items-center gap-4">
                {t("nav.settings")}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Link
              to="/settings"
              className={cn(
                buttonVariants({
                  variant: pathname == "/settings" ? "secondary" : "ghost",
                  size: "sm",
                }),
                "justify-start",
              )}
              style={
                pathname == "/settings"
                  ? {
                      backgroundColor: "var(--color-secondary)",
                      color: "var(--color-secondary-foreground)",
                    }
                  : undefined
              }
            >
              <Settings className="mr-2 h-4 w-4" />
              {t("nav.settings")}
            </Link>
          )}
          {/* Collapse/expand toggle moved to the sidebar header, where it
              replaces the logo on hover (see SidebarLayout.tsx). */}
        </nav>
      </div>
    </ScrollArea>
  );
}
